import React from 'react';
import styled from 'styled-components';

import RadioField from '@common/components/FormElements/RadioField';

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
`;

const Label = styled.label<{ isDisabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 20px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
`;

const ListItem = styled.li<{ isChecked: boolean; isDisabled: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid
    ${({ theme, isChecked }) =>
      isChecked ? theme.customColors.primary : theme.defaultColors.lightGrey};
  margin-bottom: 10px;
  border-radius: 3px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};

  p {
    display: none;
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.customColors.primary};
  }
`;

export type OptionPickerOption = {
  id: number;
  name: string;
  value: number;
  isDisabled?: boolean;
};

type OptionPickerProps<TOption extends OptionPickerOption = OptionPickerOption> = {
  options: TOption[];
  onChange: (option: TOption) => void;
  value: { id: number } | null;
  label: string;
  getOptionLabel?: (option: TOption) => React.ReactNode;
};

export const OptionPicker = <TOption extends OptionPickerOption = OptionPickerOption>({
  options,
  onChange,
  value,
  label,
  getOptionLabel,
}: OptionPickerProps<TOption>) => {
  const optionPickerId = React.useId();
  const radioGroupName = `option-picker-${optionPickerId}`;

  return (
    <>
      {label && <h3>{label}</h3>}
      <List>
        {options.map((option) => {
          const optionInputId = `${radioGroupName}-${option.id}`;
          const isDisabled = option.isDisabled ?? false;

          return (
            <ListItem isChecked={option.id === value?.id} isDisabled={isDisabled} key={option.id}>
              <Label htmlFor={optionInputId} isDisabled={isDisabled}>
                <strong>{getOptionLabel ? getOptionLabel(option) : option.name}</strong>
              </Label>
              <RadioField
                type="radio"
                name={radioGroupName}
                id={optionInputId}
                onChange={() => !isDisabled && onChange(option)}
                isChecked={option.id === value?.id}
                value={option.value}
                isDisabled={isDisabled}
              />
            </ListItem>
          );
        })}
      </List>
    </>
  );
};

export default OptionPicker;
